import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SchedulePage } from './pages/SchedulePage'
import { ClientsPage } from './pages/ClientsPage'
import { PackageTemplatesPage } from './pages/PackageTemplatesPage'
import { SettingsPage } from './pages/SettingsPage'
import { NotificationsPage } from './pages/NotificationsPage'
import { H5Page } from './pages/H5Page'
import { LandingPage } from './pages/LandingPage'
import { AboutPage } from './pages/AboutPage'
import { VerifyEmailPage } from './pages/VerifyEmailPage'
import { PrivacyPage, TermsPage } from './pages/LegalPage'
import { isAuthenticated, setAuth } from './lib/auth'
import { api } from './lib/api'
import { Layout } from './components/Layout'

/** OPT-031 收束：集中式登录守卫——除首页/H5 学员端外，所有路径统一要求登录，未登录跳首页 */
function RequireAuth() {
  // OPT-046 缺陷修复：Google OAuth 回跳是整页 302，未经前端 JS，
  // localStorage 无用户标记但服务端 cookie 已设好——此时用 me() 引导登录态，
  // 成功则补写标记放行；失败（含静默 refresh 失败）才踢回首页
  const [state, setState] = useState<'ok' | 'checking' | 'guest'>(() =>
    isAuthenticated() ? 'ok' : 'checking',
  )
  useEffect(() => {
    if (state !== 'checking') return
    let cancelled = false
    api.me()
      .then((user) => {
        if (cancelled) return
        setAuth({ id: user.id, email: user.email, name: user.name })
        setState('ok')
      })
      .catch(() => {
        if (!cancelled) setState('guest')
      })
    return () => {
      cancelled = true
    }
  }, [state])

  if (state === 'checking') return null
  if (state === 'guest') return <Navigate to="/" replace />
  return <Layout><Outlet /></Layout>
}

function App() {
  const { t, i18n } = useTranslation()

  // 同步浏览器标签页标题与 HTML lang 到当前语言
  useEffect(() => {
    document.title = `${t('app.name')} - ${t('app.tagline')}`
    document.documentElement.lang = i18n.language
  }, [t, i18n.language])

  // 订阅路由变化，确保登录/退出后重新计算认证状态
  const { pathname } = useLocation()

  return (
    <Routes>
      {/* 公开：落地页为首页；已登录访问 / 跳 /app */}
      <Route path="/" element={isAuthenticated() ? <Navigate to="/app" replace /> : <LandingPage />} />
      {/* 独立登录/注册页已删除（2026-08-07），登录/注册/忘记密码由首页弹窗承担 */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/register" element={<Navigate to="/" replace />} />

      {/* OPT-041：About Us 独立页面（顶部导航样式与首页一致），点击"关于我们"进入 */}
      <Route path="/about" element={<AboutPage />} />

      {/* OPT-051：邮箱验证落地页（公开，进页自动提交 token） */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* OPT-054：隐私政策 / 服务条款（公开，合规底线） */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />

      {/* 公开：H5 学员端 */}
      <Route path="/h5/:token" element={<H5Page />} />

      {/* 受保护：整个 /app 区域集中守卫，新增子路由天然受保护 */}
      <Route path="/app" element={<RequireAuth />}>
        <Route index element={<SchedulePage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="package-templates" element={<PackageTemplatesPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
